import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useState } from 'react'

import { AsyncStateN } from '@blade/std-lib'
import {
  tradeSettingsAtom,
  tradeSettingsModel,
} from '@root/features/trade/trade-settings/atoms'
import { openTradeSettingsDialogEvent } from '@root/features/trade/trade-settings/atoms/trade-settings-events'
import { tradeSwapTypeAtom } from '@root/features/trade/trading/atoms/trading-input'
import { tradingWalletAtom } from '@root/features/trade/trading/atoms/trading-wallet'
import { useGasSettingsValues } from '@root/features/trade/trading-controls/ui/common/use-gas-settings-values'

import { native, percent, usd } from '../portfolio-screen/money'

import type { PairModel } from '@root/entities/pair'
import type {
  BuyControlsModel,
  SellControlsModel,
} from '@root/features/trade/trading-controls/atoms/common'

/** What a buy spends, as fractions of the coin rather than of a balance. */
const BUY_AMOUNTS = [0.05, 0.1, 0.5, 1] as const
/** What a sell sells, as fractions of what is held. */
const SELL_FRACTIONS = [0.25, 0.5, 0.75, 1] as const

/**
 * Buying and selling this token.
 *
 * The same models the old panel drives — the amount, the quote, the wallet and
 * the execute action are app state, not this screen's — drawn in the board's
 * language. What changes is the order things are said in: the amount and the
 * button are what somebody came to use, so the settings that used to sit above
 * them are one quiet line underneath instead.
 *
 * The presets are the account's own, from the server. They carry the slippage
 * and the priority fee, which is why the line under the button reads them
 * rather than offering a second place to set them.
 */
export const TradePanel: React.FC<{
  pair: PairModel
  buyModel: BuyControlsModel
  sellModel: SellControlsModel
}> = memo(({ pair, buyModel, sellModel }) => {
  const [side] = useAtom(tradeSwapTypeAtom)
  const setSide = useAction(tradeSwapTypeAtom)

  const buying = side === 'buy'
  const input = buying ? buyModel.amountInputModel : sellModel.inputModel

  const [amountRaw] = useAtom(input.amountRawAtom)
  const setAmountRaw = useAction(input.setAmountRawAction)
  const setQuick = useAction(input.setQuickAmount)
  const setMax = useAction(input.setMaximumAmount)
  const [balance] = useAtom(input.balanceAtom)
  const [validation] = useAtom(
    buying ? buyModel.validationAtom : sellModel.validationAtom,
  )
  const [wallet] = useAtom(tradingWalletAtom)
  const [settings] = useAtom(tradeSettingsAtom)
  /* The settings shape differs per chain; this is the app's own reader. */
  const gas = useGasSettingsValues(settings)
  const openSettings = useAction(openTradeSettingsDialogEvent)

  const execute = useAction(
    buying
      ? buyModel.executeSwapModel.execute
      : sellModel.executeSwapModel.execute,
  )
  const [executeState] = useAtom(
    buying ? buyModel.executeSwapModel : sellModel.executeSwapModel,
  )

  const busy = AsyncStateN.isLoading(executeState)
  const spends = buying ? pair.base.ticker : pair.quote.ticker

  return (
    <section className="pn panel">
      <header>
        <h2>Trade</h2>
        <div className="right">
          <Presets />
        </div>
      </header>

      <div className="in">
        <div className="sides">
          <button
            className={buying ? 'sd buy on' : 'sd buy'}
            type="button"
            onClick={() => setSide('buy')}
          >
            Buy
          </button>
          <button
            className={buying ? 'sd sell' : 'sd sell on'}
            type="button"
            onClick={() => setSide('sell')}
          >
            Sell
          </button>
        </div>

        <label className="amount">
          <span className="lb">Amount</span>
          <input
            className="mono"
            inputMode="decimal"
            placeholder="0"
            value={amountRaw}
            onChange={(event) => setAmountRaw(event.target.value)}
          />
          <span className="unit">{spends}</span>
        </label>

        <div className="chips">
          {buying
            ? BUY_AMOUNTS.map((value) => (
                <button
                  key={value}
                  className="chip"
                  type="button"
                  onClick={() => setAmountRaw(String(value))}
                >
                  {value}
                </button>
              ))
            : SELL_FRACTIONS.map((fraction) => (
                <button
                  key={fraction}
                  className="chip"
                  type="button"
                  onClick={() => setQuick(fraction)}
                >
                  {percent(fraction * 100)}
                </button>
              ))}
          <button className="chip max" type="button" onClick={() => setMax()}>
            Max
          </button>
        </div>

        <p className="avail">
          {native(balance.toNumber())} {spends} available
          {wallet ? undefined : ' · no wallet chosen'}
        </p>

        <button
          className={buying ? 'go buy' : 'go sell'}
          type="button"
          disabled={busy || validation !== undefined || !wallet}
          onClick={() => execute()}
        >
          {busy
            ? 'Sending…'
            : `${buying ? 'Buy' : 'Sell'} ${pair.quote.ticker}`}
        </button>

        {/*
          The settings that apply, said once, where they are read rather than
          set. Changing them belongs to the preset they came from.
        */}
        <button
          className="gas mono"
          type="button"
          title="Change these"
          onClick={() => openSettings({ mode: 'drawer' })}
        >
          slippage {percent(gas.slippage)} · priority{' '}
          {gas.priorityFee === undefined ? '—' : String(gas.priorityFee)}
          {gas.bribe === undefined ? undefined : ` · tip ${String(gas.bribe)}`}
          {gas.antiMevEnabled ? ' · anti-mev' : undefined}
        </button>
      </div>
    </section>
  )
})

/** The account's own presets, each carrying its own slippage and fee. */
const Presets: React.FC = memo(() => {
  const [presetsState] = useAtom(tradeSettingsModel.networkSettingsPresetsState)
  const [activeIndex] = useAtom(tradeSettingsModel.activePresetIndex)
  const setActive = useAction(tradeSettingsModel.setActivePreset)

  if (!AsyncStateN.isSuccess(presetsState)) {
    return undefined
  }

  return (
    <div className="seg">
      {presetsState.value.presets.map((preset) => (
        <button
          key={preset.index}
          className={preset.index === activeIndex ? 'on' : undefined}
          type="button"
          title={`${preset.emoji} ${preset.name}`.trim()}
          onClick={() => setActive(preset.index)}
        >
          P{preset.index + 1}
        </button>
      ))}
    </div>
  )
})

const SPANS = ['5M', '1H', '6H', '1D'] as const

/** Green up, red down, quiet when it did not move or is not known. */
const moveColour = (moved: number) => {
  if (!Number.isFinite(moved) || moved === 0) {
    return 'var(--t2)'
  }
  return moved > 0 ? 'var(--up)' : 'var(--down)'
}

/**
 * How it is moving, and who is moving it.
 *
 * Both terminals lead their rail with this and neither of them buries it: four
 * windows across the top as tabs, each already showing its own change, so the
 * shape of the move is legible before anything is clicked — five minutes red
 * against a day green is the whole story of a token, and a tab that hides its
 * own number cannot tell it. Picking one fills the block underneath with that
 * window's volume, its buys and its sells.
 *
 * This used to sit under the trading panel showing all four at once with a bar
 * apiece. That was four bars nobody compared and no way to see what made them.
 */
export const FlowPanel: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [span, setSpan] = useState<(typeof SPANS)[number]>('5M')

  return (
    <section className="pn flowpn">
      <div className="spans">
        {SPANS.map((each) => (
          <SpanTab
            key={each}
            pair={pair}
            span={each}
            on={each === span}
            pick={() => setSpan(each)}
          />
        ))}
      </div>
      <FlowDetail key={span} pair={pair} span={span} />
    </section>
  )
})

const SpanTab: React.FC<{
  pair: PairModel
  span: (typeof SPANS)[number]
  on: boolean
  pick: () => void
}> = memo(({ pair, span, on, pick }) => {
  const [change] = useAtom(pair.stats[span].priceChange)
  const moved = Number(change)

  return (
    <button className={on ? 'sp on' : 'sp'} type="button" onClick={pick}>
      <i>{span}</i>
      <b className="mono" style={{ color: moveColour(moved) }}>
        {Number.isFinite(moved) ? percent(moved, { sign: true }) : '—'}
      </b>
    </button>
  )
})

/**
 * The chosen window, broken into the four figures that explain it.
 *
 * Volume says how much, buys and sells say who, and the bar under them says it
 * again in the one form that needs no reading. Net is the figure the other
 * three are usually being added up to reach, so it is given rather than left
 * as arithmetic.
 */
const FlowDetail: React.FC<{
  pair: PairModel
  span: (typeof SPANS)[number]
}> = memo(({ pair, span }) => {
  const stats = pair.stats[span]
  const [volume] = useAtom(stats.volumeUsd)
  const [buys] = useAtom(stats.buys)
  const [sells] = useAtom(stats.sells)
  const [buyVolume] = useAtom(stats.buyVolumeUsd)
  const [sellVolume] = useAtom(stats.sellVolumeUsd)
  const [net] = useAtom(stats.netVolumeInUsd)
  const [makers] = useAtom(stats.makers)
  const [buyers] = useAtom(stats.buyers)
  const [sellers] = useAtom(stats.sellers)

  const total = buys + sells
  const netUsd = net.toNumber()

  return (
    <div className="in">
      <div className="quad">
        <div className="qc">
          <i>Volume</i>
          <b className="mono">{usd(volume.toNumber())}</b>
        </div>
        <div className="qc">
          <i>Buys</i>
          <b className="mono" style={{ color: 'var(--up)' }}>
            {buys} <em>{usd(buyVolume.toNumber())}</em>
          </b>
        </div>
        <div className="qc">
          <i>Sells</i>
          <b className="mono" style={{ color: 'var(--down)' }}>
            {sells} <em>{usd(sellVolume.toNumber())}</em>
          </b>
        </div>
        <div className="qc">
          <i>Net</i>
          <b className="mono" style={{ color: moveColour(netUsd) }}>
            {netUsd > 0 ? '+' : ''}
            {usd(Math.abs(netUsd))}
          </b>
        </div>
      </div>

      {/* The same split once more, as the one shape that needs no reading. */}
      <span className="split" title={`${buys} buys · ${sells} sells`}>
        <i
          style={{ width: total === 0 ? '50%' : `${(buys / total) * 100}%` }}
        />
      </span>

      {/*
        How many people, under how much money. Fifty trades can be fifty
        wallets arriving or one wallet shuffling, and nothing above can tell
        those two apart.
      */}
      <div className="quad three">
        <div className="qc">
          <i>Makers</i>
          <b className="mono">{makers}</b>
        </div>
        <div className="qc">
          <i>Buyers</i>
          <b className="mono" style={{ color: 'var(--up)' }}>
            {buyers}
          </b>
        </div>
        <div className="qc">
          <i>Sellers</i>
          <b className="mono" style={{ color: 'var(--down)' }}>
            {sellers}
          </b>
        </div>
      </div>
    </div>
  )
})
